import { Key, PublicNonces } from '@borislav.itskov/schnorrkel.js';
import { useQueryClient } from '@tanstack/react-query';
import BigNumber from 'bignumber.js';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import secureLocalStorage from 'react-secure-storage';
import { io, Socket } from 'socket.io-client';
import { decrypt, safeJSONParse } from 'src/helpers/encryption';
import { storeSignature } from 'src/helpers/save-signatures';
import { accessTokenIAO } from 'src/helpers/storage';
import { useAuth } from 'src/libs/rainbow';
import Signer from 'src/services/auth/key-pair';
import { orderKeys } from 'src/services/order/order-keys';
import { useAccount } from 'wagmi';
import { useSigner } from './SignerContext';
import { debounce, throttle } from 'lodash';

const URL = process.env.REACT_APP_SERVICE_SOCKET ?? '';

interface AssetInfo {
  tokenName: string;
  balance: number;
}

interface SocketContextProps {
  signatureSocket: Socket | null;
  isSignatureSocketInitialized: boolean;
  isBalanceSocketInitialized: boolean;
  userBalance: number;
  userBalanceAvailable: number;
  userLPBalance: number;
  userAssets: Array<AssetInfo>;
  totalUnrealizedPnl: number;
  openPositionsCount: number;
  openOrdersCount: number;
}

const SocketContext = createContext<SocketContextProps | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
}

const reconnectInterval = 3000;
const maxReconnectAttempts = 10;
const positionCountUpdateTime = 1000;
const positionUpdateTime = 3000;

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const [signatureSocket, setSignatureSocket] = useState<Socket | null>(null);

  const reconnectAttemptsRef = useRef(0);
  const isReconnectingRef = useRef(false); // Prevent multiple calls

  const [userBalance, setUserBalance] = useState(0);
  const [userBalanceAvailable, setUserBalanceAvailable] = useState(0);
  const [userLPBalance, setUserLPBalance] = useState(0);
  const { getPublicNonces, getPublicKey, multiSignMessage, signer } = useSigner();
  const [isSignatureSocketInitialized, setIsSignatureSocketInitialized] = useState(false);
  const [isBalanceSocketInitialized, setIsBalanceSocketInitialized] = useState(false);
  const { isAuthenticated } = useAuth();
  const { address } = useAccount();
  const balanceSocket = useRef<WebSocket | null>(null);
  const [userAssets, setUserAssets] = useState<Array<AssetInfo>>([]);
  const [totalUnrealizedPnl, setTotalUnrealizedPnl] = useState<number>(0);
  const [openPositionsCount, setOpenPositionsCount] = useState<number>(0);
  const [openOrdersCount, setOpenOrdersCount] = useState<number>(0);

  const updateOpenOrderCount = useCallback((count: number) => {
    setOpenOrdersCount(count);
  }, []);
  const handleOpenOrderCountChange = throttle(updateOpenOrderCount, positionCountUpdateTime, {
    leading: true,
    trailing: true,
  });

  const updatePositionCount = useCallback((count: number) => {
    setOpenPositionsCount(count);
  }, []);
  const handlePositionCountChange = throttle(updatePositionCount, positionCountUpdateTime, {
    leading: true,
    trailing: true,
  });

  const updatePosition = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: orderKeys.position(address!) });
  }, [address]);
  const handleUpdatePosition = debounce(updatePosition, positionUpdateTime);

  const closeWebSocket = () => {
    if (balanceSocket.current) {
      console.log('Closing WebSocket connection.');
      balanceSocket.current.close();
      balanceSocket.current = null;
    }

    if(signatureSocket){
      signatureSocket.close();
      setSignatureSocket(null)
    }
  };

  const initBalanceWebSocket = useCallback(
    (token: string) => {
      if (isReconnectingRef.current) {
        return;
      }
      if (!balanceSocket.current) {
        const newWs = new WebSocket(`${URL}/api-gateway/v1/ws?accessToken=${token}`);
        balanceSocket.current = newWs;

        newWs.onopen = () => {
          reconnectAttemptsRef.current = 0;
          const balanceRequest = {
            event: 'balance.update',
            params: {},
          };
          const lpRequest = {
            event: 'lp.balance.update',
            params: {},
          };
          const positionCountRequest = {
            event: 'position.count.update',
            params: {},
          };
          const orderCountRequest = {
            event: 'order.count.update',
            params: {},
          };
          newWs.send(JSON.stringify(balanceRequest));
          newWs.send(JSON.stringify(lpRequest));
          newWs.send(JSON.stringify(positionCountRequest));
          newWs.send(JSON.stringify(orderCountRequest));
        };

        newWs.onmessage = (event) => {
          const data = JSON.parse(event.data);
          console.log('Received data:', data);
          if (data.event === 'balance.update' && data.success) {
            const balanceInfo = new BigNumber(data.result[0]?.balance || 0)
              .plus(data?.result[0]?.position_margins || 0)
              .plus(data?.result[0]?.open_orders || 0)
              .toNumber();

            setUserBalance(balanceInfo ?? 0);
            // [CROSS_MARGIN_LAZY_ALLOC_QOL] Use available field which accounts for reserved cross margin (QOL)
            setUserBalanceAvailable(data.result[0].available || data.result[0].balance);

            // Extract and combine assets with position initial margins
            const assets: Array<{ tokenName: string; balance: number }> = [];
            const assetMap = new Map<string, number>();

            // Add regular assets
            if (data.result[0]?.assets && Array.isArray(data.result[0].assets)) {
              data.result[0].assets.forEach((asset: { token_name: string, balance: number }) => {
                assetMap.set(asset.token_name, (assetMap.get(asset.token_name) || 0) + asset.balance);
              });
            }

            // Add position initial margins to the corresponding assets
            if (data.result[0]?.position_initial_margins && Array.isArray(data.result[0].position_initial_margins)) {
              data.result[0].position_initial_margins.forEach((margin: { token_name: string, balance: number }) => {
                assetMap.set(margin.token_name, (assetMap.get(margin.token_name) || 0) + margin.balance);
              });
            }

            // Convert map back to array format
            assetMap.forEach((balance, tokenName) => {
              // Only show assets with balance > 0 to match the admin panel behavior
              if (balance > 0) {
                assets.push({
                  tokenName,
                  balance
                });
              }
            });
            setUserAssets(assets);
            if (data.result[0]?.unrealized_pnl !== undefined) {
              setTotalUnrealizedPnl(data.result[0].unrealized_pnl);
            }

            // Update counts if available
            if (data.result[0]?.open_positions_count !== undefined) {
              handlePositionCountChange(data.result[0].open_positions_count);
            }

            if (data.result[0]?.open_orders_count !== undefined) {
              handleOpenOrderCountChange(data.result[0].open_orders_count);
            }

            if (balanceInfo !== undefined) {
              setIsBalanceSocketInitialized(true);
            }
          }
          if (data.event === 'lp.balance.update' && data.success) {
            const balanceInfo = data.result[0].balance;
            setUserLPBalance(balanceInfo ?? 0);
          }
          if (data.event === 'position.count.update' && data.success) {
            const positionCount = data.result?.open_positions_count ?? 0;
            handlePositionCountChange(positionCount);
            console.log('Position count updated:', positionCount);
          }
          if (data.event === 'order.count.update' && data.success) {
            const orderCount = data.result?.open_orders_count ?? 0;
            handleOpenOrderCountChange(orderCount);
            console.log('Order count updated:', orderCount);
          }
        };

        newWs.onclose = () => {
          if (balanceSocket.current) {
            console.log('ws closed by server');
          } else {
            console.log('ws closed by app component unmount');
            return;
          }
          if (isReconnectingRef.current) {
            console.log('Reconnection in progress, ignoring close handler.');
            return;
          }
          setIsBalanceSocketInitialized(false);
          console.log('WebSocket connection closed. Attempting to reconnect...');

          attemptReconnect(token);
        };

        newWs.onerror = (error) => {
          console.error('WebSocket error:', error);
          newWs.close();
        };

        return () => {
          balanceSocket.current = null;
          newWs.close();
        };
      }
    },
    [URL, balanceSocket, setIsBalanceSocketInitialized],
  );
  const attemptReconnect = useCallback(
    (token: string) => {
      const accessToken = localStorage.getItem(accessTokenIAO) ?? token;

      if (isReconnectingRef.current) {
        console.log('Reconnect already in progress. Skipping...');
        return;
      }
      isReconnectingRef.current = true;

      if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        console.log('Max reconnect attempts reached. Please refresh the page to try again.');
        isReconnectingRef.current = false;
        window.location.reload(); // Reload the page when the reconnect limit is exceeded.
        return;
      }

      reconnectAttemptsRef.current += 1;
      console.log(
        `Reconnecting attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts}...`,
      );

      setTimeout(() => {
        isReconnectingRef.current = false;
        balanceSocket.current = null;
        initBalanceWebSocket(accessToken);
      }, reconnectInterval);
    },
    [maxReconnectAttempts, reconnectInterval, initBalanceWebSocket],
  );

  const initSignatureSocket = (token: string) => {
    if (signatureSocket) return;

    const newSocket = io(URL, {
      path: '/signature-service/ws/',
      extraHeaders: {
        Authorization: `Bearer ${token}`,
      },
      reconnection: true,
      reconnectionAttempts: maxReconnectAttempts,
      reconnectionDelay: reconnectInterval,
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      newSocket.connect();
    });

    newSocket.on('disconnect', (reason) => {
      console.warn('Socket disconnected:', reason);
      if (reason !== 'io client disconnect') {
        newSocket.connect();
      }
    });

    newSocket.on('reconnect_attempt', (attempt) => {
      console.log(`Reconnection attempt ${attempt}`);
    });

    setSignatureSocket(newSocket);
    setIsSignatureSocketInitialized(true);
  };

  const generateNonceToSend = (data: any) => {
    const storedData = secureLocalStorage.getItem(`signerData-${address}`) as string;
    const decryptedData = decrypt(storedData, 'vDex', address as string);
    let privateKey;
    if (decryptedData) {
      const parsedData = safeJSONParse(decryptedData);
      // console.log('🚀 ~ generateNonceToSend ~ parsedData:', parsedData);

      if (parsedData && parsedData.privateKey) {
        privateKey = parsedData.privateKey;
      } else {
        // console.error('Failed to parse decrypted data or missing privateKey');
        // Handle this error case - maybe clear the corrupted data and generate new keys
      }
    } else {
      // console.error('Decryption failed');
      // Handle this error case - maybe clear the corrupted data and generate new keys
    }
    // const { privateKey } = JSON.parse(decryptedData!);
    const localSigner = new Signer(privateKey!);
    signersData[data.id] = localSigner;
    const nonces = localSigner.getPublicNonces();
    // console.log('🚀 ~ onNewTrade ~ nonces:', nonces);
    const publicKey = localSigner.getPublicKey()?.toHex();
    // console.log('🚀 ~ onNewTrade ~ publicKey:', publicKey);
    const sigData = {
      publicKey: publicKey,
      publicNonce: {
        kPublic: localSigner.getPublicNonces()?.kPublic.toHex(),
        kTwoPublic: localSigner.getPublicNonces()?.kTwoPublic.toHex(),
      },
      signatureID: data.id,
    };

    return sigData;
  };
  let signersData: Signer[] = [];
  // console.log('🚀 ~ signersData:', signersData);
  useEffect(() => {
    if (signatureSocket) {
      const onConnect = () => {
        // console.log('socket connect');
        signatureSocket.emit('onConnect', 'connected to server');
      };

      const onDisconnect = () => { };

      const onConnectReply = (data: any) => {
        // console.log('socket onconnect reply', data, signatureSocket.id);
        const signatures = data?.signatures;

        if (signatures.length > 0) {
          signatures.map((sig: any) => {
            const sigData = generateNonceToSend(sig);
            // console.log('🚀 ~ signatures.map ~ sigData:', sigData, signersData);
            signatureSocket.emit('exchangeNonce', sigData);
          });
        }
      };

      const onNewTrade = (data: any) => {
        const sigData = generateNonceToSend(data);
        handleUpdatePosition();
        signatureSocket.emit('exchangeNonce', sigData);
      };

      const onExchangeNonceReply = (signRequestDto: any) => {
        // console.log(
        //   'socket onexchange',
        //   signRequestDto,
        //   signersData,
        //   signersData[signRequestDto.signatureID].getAddress(),
        //   signRequestDto.signatureID,
        // );
        // console.log('socket onexchange', signersData, signRequestDto?.signatureID);
        try {
          const nonces = {
            kPublic: Key.fromHex(signRequestDto.publicNonce.kPublic),
            kTwoPublic: Key.fromHex(signRequestDto.publicNonce.kTwoPublic),
          };
          const publicKey = Key.fromHex(signRequestDto.publicKey);
          const publicKeys = [publicKey, signersData[signRequestDto.signatureID].getPublicKey()];
          const platformNonce = signersData[signRequestDto.signatureID].getPublicNonces();
          const publicNonces = [nonces, platformNonce];
          const signature = multiSignMessage(
            signRequestDto.signatureData,
            publicKeys as Key[],
            publicNonces as PublicNonces[],
            signersData[signRequestDto.signatureID],
          );
          const signRes = {
            signature: signature!.signature.toHex(),
            publicKey: signersData[signRequestDto.signatureID].getPublicKey()!.toHex(),
            publicNonce: {
              kPublic: platformNonce!.kPublic.toHex(),
              kTwoPublic: platformNonce!.kTwoPublic.toHex(),
            },
            signatureID: signRequestDto.signatureID,
          };
          signatureSocket.emit('generateSignature', signRes);
        } catch (error) { }
      };

      const onGenerateSignatureReply = (data: any) => {
        // console.log('socket generatesignaturereply', data);
        signersData = [];
        storeSignature(JSON.stringify(data), address!);
        // localStorage.setItem('lastSignature', JSON.stringify(data));
      };

      signatureSocket.on('connect', onConnect);
      signatureSocket.on('disconnect', onDisconnect);
      signatureSocket.on('onConnectReply', onConnectReply);
      signatureSocket.on('new-trade-events', onNewTrade);
      signatureSocket.on('exchangeNonceReply', onExchangeNonceReply);
      signatureSocket.on('generateSignatureReply', onGenerateSignatureReply);

      // socket.on('message', onListen);
      return () => {
        signatureSocket.off('connect', onConnect);
        signatureSocket.off('disconnect', onDisconnect);
        signatureSocket.off('onConnectReply', onConnectReply);
        signatureSocket.off('new-trade-events', onNewTrade);
        signatureSocket.off('exchangeNonceReply', onExchangeNonceReply);
        signatureSocket.off('generateSignatureReply', onGenerateSignatureReply);
        // socket!.off('message', onListen);
      };
    }
  }, [signatureSocket, signer]);

  useEffect(() => {
    const token = localStorage.getItem(accessTokenIAO);
    // console.log('🚀 ~ useEffect ~ token:', token);
    if (token && isAuthenticated) {
      initSignatureSocket(token);
      initBalanceWebSocket(token);
    }
  }, [isSignatureSocketInitialized, isAuthenticated, initBalanceWebSocket]);

  useEffect(() => {
    if (!isAuthenticated) {
      closeWebSocket();
    }
  }, [isAuthenticated, balanceSocket]);

  return (
    <SocketContext.Provider
      value={{
        signatureSocket,
        isSignatureSocketInitialized,
        isBalanceSocketInitialized,
        userBalance,
        userBalanceAvailable,
        userLPBalance,
        userAssets,
        totalUnrealizedPnl,
        openPositionsCount,
        openOrdersCount,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = (): SocketContextProps => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};
